import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, Grid, Typography, IconButton, Checkbox, FormControlLabel, Autocomplete, TextField } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import styled from "@emotion/styled";
import { Edit, Check, Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../redux/slices/users";
import { useHasPermission } from "../utils/permissionUtils";
import { TIMEZONES } from "../utils/timezones";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const BoxContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    marginTop: theme.spacing(2),
    height: "100%",
}));

const BoxHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
}));

const BoxTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    textAlign: "left",
    marginRight: theme.spacing(2),
}));

const DataSet = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1),
}));

const Row = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
}));

const RowLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    marginRight: theme.spacing(2),
}));

const RowValue = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
}));

const DayRow = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
}));

const DayToggleLabel = styled(FormControlLabel)(() => ({
    minWidth: 140,
    margin: 0,
}));

const TimeFieldWrap = styled(Box)(() => ({
    flex: 1,
    minWidth: 110,
}));

const ErrorText = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
    fontSize: theme.typography.size.smd,
    marginTop: theme.spacing(0.5),
}));

const parseTime = (s) => {
    if (!s || typeof s !== "string") return null;
    const trimmed = s.trim();
    if (!trimmed) return null;
    let h, m;
    if (trimmed.includes(":")) {
        const [hPart, mPart] = trimmed.split(":");
        if (hPart === undefined || mPart === undefined) return null;
        h = parseInt(hPart, 10);
        m = parseInt(mPart, 10);
    } else {
        h = parseInt(trimmed, 10);
        m = 0;
    }
    if (!Number.isInteger(h) || h < 0 || h > 23) return null;
    if (!Number.isInteger(m) || m < 0 || m > 59) return null;
    const hhmm = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    const d = dayjs(`1970-01-01T${hhmm}`);
    return d.isValid() ? d : null;
};

const formatTime = (d) => (d && d.isValid && d.isValid() ? d.format("HH:mm") : "");

const buildEditState = (workingHours) => {
    const byDay = new Map((workingHours || []).map((wh) => [wh.day, wh]));
    return DAYS.map((day) => {
        const existing = byDay.get(day);
        const startDayjs = existing ? parseTime(existing.start) : null;
        const endDayjs = existing ? parseTime(existing.end) : null;
        return {
            day,
            enabled: Boolean(existing),
            start: startDayjs ? startDayjs.format("HH:mm") : "09:00",
            end: endDayjs ? endDayjs.format("HH:mm") : "17:00",
        };
    });
};

const ProfileAvailabilityBox = ({ user = {}, onUserChange }) => {
    const [workingHours, setWorkingHours] = useState(user.workingHours || []);
    const [timezone, setTimezone] = useState(user.timezone || "UTC");
    const [editState, setEditState] = useState(() => buildEditState(user.workingHours));
    const [editTimezone, setEditTimezone] = useState(user.timezone || "UTC");
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState("");

    const hasPermission = useHasPermission();
    const canEditUser = hasPermission("editUsers");
    const isSelf = user._id === useSelector((state) => state.auth.userId);
    const dispatch = useDispatch();

    useEffect(() => {
        if (isEditing) return;
        setWorkingHours(user.workingHours || []);
        setTimezone(user.timezone || "UTC");
    }, [user._id, user.workingHours, user.timezone, isEditing]);

    const today = useMemo(() => {
        try {
            return new Date().toLocaleString("en-US", { weekday: "long", timeZone: timezone || "UTC" });
        } catch {
            return new Date().toLocaleString("en-US", { weekday: "long" });
        }
    }, [timezone]);

    const todayEntry = workingHours.find((wh) => wh.day === today);

    const handleEdit = () => {
        setEditState(buildEditState(workingHours));
        setEditTimezone(timezone || "UTC");
        setError("");
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError("");
    };

    const validate = (rows) => {
        for (const r of rows) {
            if (!r.enabled) continue;
            const s = parseTime(r.start);
            const e = parseTime(r.end);
            if (!s || !e) return `Invalid time on ${r.day}`;
            if (!e.isAfter(s)) return `End must be after start on ${r.day}`;
        }
        return "";
    };

    const handleSave = () => {
        const err = validate(editState);
        if (err) {
            setError(err);
            return;
        }
        const payload = editState
            .filter((r) => r.enabled)
            .map((r) => ({ day: r.day, start: r.start, end: r.end }));

        dispatch(
            updateUser({
                userId: user._id,
                userData: { workingHours: payload, timezone: editTimezone },
                callback: (response) => {
                    if (response?.user) {
                        setWorkingHours(response.user.workingHours || []);
                        setTimezone(response.user.timezone || "UTC");
                        setIsEditing(false);
                        setError("");
                        if (typeof onUserChange === "function") onUserChange(response.user);
                    }
                },
            })
        );
    };

    const updateRow = (idx, patch) => {
        setEditState((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };

    const canEdit = isSelf || canEditUser;

    return (
        <Grid item xs={12} md={4} lg={4}>
            <BoxContainer>
                <BoxHeader>
                    <BoxTitle variant="h5">Availability</BoxTitle>
                    {canEdit &&
                        (isEditing ? (
                            <Box>
                                <IconButton onClick={handleSave}>
                                    <Check />
                                </IconButton>
                                <IconButton onClick={handleCancel}>
                                    <Close />
                                </IconButton>
                            </Box>
                        ) : (
                            <IconButton onClick={handleEdit}>
                                <Edit />
                            </IconButton>
                        ))}
                </BoxHeader>

                <DataSet>
                    {!isEditing && (
                        <>
                            <Row>
                                <RowLabel>Status:</RowLabel>
                                <Chip
                                    variant="outlined"
                                    label={user.isAvailable ? "Available" : "Unavailable"}
                                    size="small"
                                    color={user.isAvailable ? "success" : "error"}
                                />
                            </Row>
                            <Row>
                                <RowLabel>Working:</RowLabel>
                                <Chip
                                    variant="outlined"
                                    label={user.isWorking ? "Yes" : "No"}
                                    size="small"
                                    color={user.isWorking ? "success" : "error"}
                                />
                            </Row>
                            <Row>
                                <RowLabel>Timezone:</RowLabel>
                                <RowValue>{timezone || "UTC"}</RowValue>
                            </Row>
                            <Row>
                                <RowLabel>Today:</RowLabel>
                                <RowValue>
                                    {todayEntry ? `${todayEntry.start} - ${todayEntry.end}` : "Off"}
                                </RowValue>
                            </Row>
                        </>
                    )}

                    {isEditing && (
                        <>
                            <Autocomplete
                                value={editTimezone}
                                onChange={(_, value) => setEditTimezone(value || "UTC")}
                                options={TIMEZONES}
                                disableClearable
                                size="small"
                                renderInput={(params) => <TextField {...params} label="Timezone" />}
                            />
                            {editState.map((row, idx) => (
                                <DayRow key={row.day}>
                                    <DayToggleLabel
                                        control={
                                            <Checkbox
                                                checked={row.enabled}
                                                onChange={(e) => updateRow(idx, { enabled: e.target.checked })}
                                            />
                                        }
                                        label={row.day}
                                    />
                                    <TimeFieldWrap>
                                        <TimePicker
                                            label="Start"
                                            value={parseTime(row.start)}
                                            ampm={false}
                                            disabled={!row.enabled}
                                            onChange={(d) => updateRow(idx, { start: formatTime(d) })}
                                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                                        />
                                    </TimeFieldWrap>
                                    <TimeFieldWrap>
                                        <TimePicker
                                            label="End"
                                            value={parseTime(row.end)}
                                            ampm={false}
                                            disabled={!row.enabled}
                                            onChange={(d) => updateRow(idx, { end: formatTime(d) })}
                                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                                        />
                                    </TimeFieldWrap>
                                </DayRow>
                            ))}
                            {error && <ErrorText>{error}</ErrorText>}
                        </>
                    )}
                </DataSet>
            </BoxContainer>
        </Grid>
    );
};

export default ProfileAvailabilityBox;
